import Phaser from 'phaser';
import { GOLD_TEXT_COLOR, ItemRarity } from '@shared/constants';

/**
 * Color canónico para cualquier texto/número que represente oro. Alias del
 * `GOLD_TEXT_COLOR` de `@shared/constants` re-exportado acá para que los
 * call-sites importen color + helpers del mismo lugar.
 */
export const GOLD_COLOR = GOLD_TEXT_COLOR;

/**
 * Formatea un entero con punto como separador de miles (es_AR / es_ES):
 * `1234567` → `1.234.567`. Negativos preservan el signo. Decimales se
 * redondean al entero más cercano (los precios del juego son enteros).
 */
export function formatThousands(n: number): string {
  const sign = n < 0 ? '-' : '';
  return sign + String(Math.abs(Math.round(n))).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
}

/**
 * Registry de atlas de íconos UI del juego.
 *
 * Cada atlas es una PNG en `client/assets/icon/iconsN.png` con grid de celdas
 * cuadradas uniformes (16×16 por convención). Los frames se referencian por un
 * nombre semántico global (ej. `gold`, `close`, `defense`) — el resolver
 * encuentra automáticamente en qué atlas vive cada nombre. Los call-sites no
 * importan texture keys, solo nombres.
 *
 * Convención: las coordenadas (row, col) en el código y comentarios son
 * 1-indexed — más natural para hablar con humanos. El frame index real de
 * Phaser es 0-indexed y se calcula con `frameOf(row, col, cols)`.
 *
 * Cómo agregar UN ÍCONO NUEVO (mismo atlas):
 *   1. Identificá la celda en `iconsN.png` (1-indexed: row=1 arriba, col=1 izq).
 *   2. Sumá una entrada al `icons` del atlas correspondiente en `ICON_ATLASES`
 *      con `frameOf(row, col, atlas.cols)`.
 *   3. Llamalo con `createUIIcon(scene, x, y, 'nombre')` desde cualquier UI.
 *
 * Cómo agregar UN ATLAS NUEVO:
 *   1. Dropear `iconsN.png` en `client/assets/icon/` (celdas 16×16, RGBA).
 *   2. Agregar un objeto a `ICON_ATLASES` con `key`, `asset`, `cols`, `rows`,
 *      `cellSize` y `icons` (mapping inicial, puede ir vacío y completarse
 *      después).
 *   3. El loop de preload en `GameScene` y el resolver global hacen el resto.
 *
 * Nombres únicos: si un nombre está repetido entre atlases, gana el primero
 * en `ICON_ATLASES`. `validateIconAtlases()` se ejecuta una vez al boot y
 * avisa por consola si hay colisiones.
 */

/** Devuelve el frame index 0-based a partir de (row, col) 1-based dado el ancho del grid. */
export function frameOf(row: number, col: number, cols: number): number {
  return (row - 1) * cols + (col - 1);
}

export interface IconAtlasConfig {
  /** Texture key de Phaser. */
  key: string;
  /** Path del asset, relativo a `/`. */
  asset: string;
  /** Columnas y filas del grid. */
  cols: number;
  rows: number;
  /** Tamaño de celda en px (frameWidth = frameHeight). */
  cellSize: number;
  /** Mapping nombre semántico → frame index dentro de ESTE atlas. */
  icons: Record<string, number>;
}

/**
 * Registry global de atlases. Para sumar un atlas nuevo, agregar un objeto acá.
 *
 * Por convención los nombres semánticos viven en inglés snake_case.
 */
export const ICON_ATLASES: IconAtlasConfig[] = [
  {
    // Atlas grande (futuro primario). Frames source 64×64 — el displaySize
    // del sprite los renderiza al tamaño UI default (24 px). Va PRIMERO en el
    // array para que, durante la migración progresiva, cualquier nombre que se
    // mapee acá automáticamente le gane al equivalente en atlas 1 o 2.
    // Mapping inicial vacío; se completa via el viewer (Ctrl+I).
    key: 'ui_icons_3',
    asset: '/assets/icon/icons3.png',
    cols: 16,
    rows: 137,
    cellSize: 64,
    icons: {
      // Currency — `gold` vive en ui_icons_1 (1,10,2); NO mapear acá para no
      // ganarle por orden de atlas.

      // UI / Sections
      settings: frameOf(1, 2, 16),
      character_info: frameOf(1, 5, 16),
      loot: frameOf(1, 6, 16),
      speed: frameOf(1, 7, 16),
      map: frameOf(1, 8, 16),
      group: frameOf(1, 14, 16),
      dungeon: frameOf(2, 8, 16),
      spawn: frameOf(3, 4, 16),
      monster_stats: frameOf(7, 10, 16),
      critical: frameOf(10, 10, 16),
      backpack: frameOf(10, 14, 16),

      // NPC types / acciones de interacción
      learn: frameOf(7, 8, 16),
      npc_vendor: frameOf(8, 12, 16),
      craft: frameOf(8, 13, 16),
      repair: frameOf(8, 14, 16),
      sell_buy: frameOf(10, 16, 16),
      npc_priest: frameOf(42, 14, 16),
      disable: frameOf(57, 16, 16),
      upgrade: frameOf(58, 10, 16),
      whisper: frameOf(60, 6, 16),

      // Rarezas de items (fila 133)
      rarity_common: frameOf(133, 4, 16),
      rarity_chaos: frameOf(133, 5, 16),
      rarity_legendary: frameOf(133, 6, 16),
      rarity_rare: frameOf(133, 7, 16),
      rarity_set: frameOf(133, 8, 16),
      rarity_epic: frameOf(133, 9, 16),

      // Resources (vitals)
      hp: frameOf(38, 3, 16),
      mana: frameOf(38, 4, 16),
      hp_regen: frameOf(39, 3, 16),
      mana_regen: frameOf(39, 4, 16),
      stamina: frameOf(42, 10, 16),
      evasion: frameOf(42, 11, 16),
      magic_find: frameOf(42, 12, 16),
      reflect: frameOf(44, 3, 16),
      lifesteal: frameOf(44, 10, 16),
      healing: frameOf(45, 5, 16),
      attack_speed: frameOf(46, 12, 16),

      // Spell stats
      cooldown: frameOf(49, 3, 16),
      cast_time: frameOf(50, 5, 16),
      target_enemy: frameOf(68, 8, 16),
      target_self: frameOf(68, 9, 16),
      target_ally: frameOf(68, 11, 16),
      target_ground: frameOf(68, 14, 16),
      area: frameOf(78, 15, 16),
      range: frameOf(79, 1, 16),

      // Habilidades (skills). Ver docs/SKILLS_SYSTEM.md. Reutilizados de otros
      // atlas donde ya existen (armas en ui_icons_2, boots en ui_icons_1).
      skill_knuckles: frameOf(8, 4, 16), // nudilleras (Ω)
      skill_mace: frameOf(8, 7, 16), // maza / maul
      skill_mining: frameOf(12, 6, 16), // pico
      skill_woodcutting: frameOf(7, 13, 16), // tronco
      skill_carpentry: frameOf(13, 13, 16), // tablones
      skill_crafting: frameOf(13, 12, 16), // cuero / pellejo
      skill_tailoring: frameOf(7, 9, 16), // rollo de tela
      skill_fishing: frameOf(23, 9, 16), // pez
      skill_cooking: frameOf(17, 13, 16), // carne cocida
      skill_herblore: frameOf(16, 10, 16), // hierba
      skill_farming: frameOf(16, 4, 16), // brote / planta
      skill_construction: frameOf(1, 16, 16), // choza / edificio
      skill_taming: frameOf(1, 15, 16), // cornamenta (bestia)
      skill_hunter: frameOf(15, 7, 16), // garra
      skill_prayer: frameOf(1, 12, 16), // estrella (fe)
      star: frameOf(1, 12, 16), // estrella dorada — usada como "favorito" en UI
      wiki_book: frameOf(1, 9, 16), // libro/tomo — usado como "info/wiki" en UI
      pin_flag: frameOf(3, 11, 16), // banderín rojo — usado como "fijar/marcar" (no hay chincheta)
      skill_thieving: frameOf(10, 15, 16), // bolsa de oro

      // Dev-tools de la barra del login (reemplazan los emojis). Ver AuthUI.makeDevToolButton.
      tool_world: frameOf(2, 2, 16), // montaña → World Builder (terreno macro)
      tool_tree: frameOf(2, 6, 16), // árbol → Passive Tree Editor
      tool_sprite: frameOf(3, 5, 16), // cara/personaje → Sprite Frame Editor
      tool_surface: frameOf(3, 8, 16), // hoja/pasto → Surface Indexer
      tool_particle: frameOf(2, 10, 16), // cristal → Particle Editor
    },
  },
  {
    key: 'ui_icons_1',
    asset: '/assets/icon/icons1.png',
    cols: 6,
    rows: 11,
    cellSize: 16,
    icons: {
      // Currency / wealth
      gold: frameOf(10, 2, 6),

      // Combat stats
      physical_damage: frameOf(9, 1, 6),
      defense: frameOf(9, 2, 6),
      movement_speed: frameOf(9, 3, 6),

      // Equipment slots
      helmet: frameOf(7, 5, 6),
      armor: frameOf(7, 6, 6),
      boots: frameOf(8, 4, 6),
      potion: frameOf(8, 5, 6),
      ring: frameOf(9, 6, 6),
      amulet: frameOf(10, 1, 6),

      // UI / controls
      chat: frameOf(2, 4, 6),
      options: frameOf(4, 6, 6),
      close: frameOf(3, 5, 6),
      locked: frameOf(1, 4, 6),

      // Navigation arrows
      arrow_up: frameOf(5, 3, 6),
      arrow_down: frameOf(5, 4, 6),
      arrow_left: frameOf(5, 5, 6),
      arrow_right: frameOf(5, 6, 6),

      // Activities / meta
      quest: frameOf(5, 2, 6),
      tournaments: frameOf(4, 2, 6),
      death: frameOf(1, 1, 6),

      // Placeholder visual para slots vacíos o sin contenido aún.
      placeholder: frameOf(8, 6, 6),
    },
  },
  {
    // Atlas de items / armas / stats. Coords (row, col) 1-indexed.
    // Los nombres de armas calzan con `WeaponSubtype` (shared/types/item.ts).
    key: 'ui_icons_2',
    asset: '/assets/icon/icons2.png',
    cols: 8,
    rows: 8,
    cellSize: 16,
    icons: {
      // Weapons — matchean con `WeaponSubtype.*`
      sword: frameOf(2, 1, 8),
      dagger: frameOf(3, 1, 8),
      axe: frameOf(5, 3, 8),
      axe_2h: frameOf(7, 1, 8),
      staff: frameOf(5, 2, 8),
      wand: frameOf(2, 8, 8),
      bow: frameOf(1, 4, 8),
      crossbow: frameOf(6, 4, 8),
      throwable: frameOf(8, 3, 8),

      // Accesorios de combate
      shield: frameOf(6, 2, 8),
      arrow: frameOf(3, 4, 8),
      quiver: frameOf(4, 4, 8),

      // Combat stats
      magic_damage: frameOf(3, 7, 8),
      crit_chance: frameOf(5, 1, 8),
      magic_crit: frameOf(6, 8, 8),

      // Meta / currency
      experience: frameOf(8, 7, 8),
      bronze: frameOf(6, 5, 8),
      // gold (6,6) intencionalmente NO mapeado: ya existe en atlas 1.
    },
  },
];

/**
 * Mapeo `ItemModifiers` key → nombre de ícono UI. Usado por los paneles que
 * renderizan líneas de stat (`+5 minDamage`, etc.) para prefijar con el ícono
 * correspondiente.
 *
 * Devuelve `null` si el stat no tiene ícono asignado.
 */
const STAT_ICON_MAP: Record<string, string> = {
  // Damage / defense
  minDamage: 'physical_damage',
  maxDamage: 'physical_damage',
  physDef: 'defense',
  magicDef: 'defense',
  // Speed
  moveSpeed: 'movement_speed',
  attackSpeed: 'attack_speed',
  castSpeed: 'cast_time',
  // Crit
  critChance: 'critical',
  critMultiplier: 'critical',
  // Vitals (pools)
  hp: 'hp',
  mana: 'mana',
  stamina: 'stamina',
  // Vitals / regen
  regenHP: 'hp_regen',
  regenMana: 'mana_regen',
  regenStamina: 'stamina',
  // Combat advanced
  lifeStealPhysical: 'lifesteal',
  lifeStealMagic: 'lifesteal',
  reflectPhysicalDamage: 'reflect',
  reflectMagicDamage: 'reflect',
  healingOutput: 'healing',
  healingReceived: 'healing',
  magicFind: 'magic_find',
  evasion: 'evasion',
  // Bonus de Pesca (placeholder del futuro Skill de Pesca): le damos el ícono de
  // pez del atlas para que deje de mostrarse sin ícono. Ver docs/FISHING_SKILL.md.
  fishingBonus: 'skill_fishing',
};

export function getStatIconName(statKey: string): string | null {
  return STAT_ICON_MAP[statKey] ?? null;
}

/**
 * Mapeo `ItemRarity` → nombre de ícono de rareza. CHAOS no existe como rareza
 * (el ícono `rarity_chaos` queda registrado pero sin uso).
 */
const RARITY_ICON_MAP: Record<number, string> = {
  [ItemRarity.COMMON]: 'rarity_common',
  [ItemRarity.RARE]: 'rarity_rare',
  [ItemRarity.EPIC]: 'rarity_epic',
  [ItemRarity.LEGENDARY]: 'rarity_legendary',
  [ItemRarity.SET]: 'rarity_set',
};

/** Ícono de rareza por valor de `ItemRarity`, o null si no está mapeado. */
export function getRarityIconName(rarity: number): string | null {
  return RARITY_ICON_MAP[rarity] ?? null;
}

/**
 * Ícono por tipo de NPC, derivado de los flags del template
 * (`shared/data/npcs.ts`). Prioridad: priest > spell vendor > item merchant.
 * Devuelve null para mobs / NPCs genéricos sin rol con ícono.
 */
export function getNpcTypeIconName(
  t: { merchantId?: string; spellVendorId?: string; priestService?: boolean } | null | undefined,
): string | null {
  if (!t) return null;
  if (t.priestService) return 'npc_priest';
  if (t.spellVendorId) return 'learn';
  if (t.merchantId) return 'npc_vendor';
  return null;
}

/**
 * Mapeo modKey → label legible en español para mostrar en tooltips/detail
 * panels en lugar del key crudo. Lo que no esté acá cae al key crudo.
 */
const STAT_LABEL_ES_MAP: Record<string, string> = {
  minDamage: 'Daño mínimo',
  maxDamage: 'Daño máximo',
  physDef: 'Defensa física',
  magicDef: 'Defensa mágica',
  moveSpeed: 'Velocidad de movimiento',
  attackSpeed: 'Velocidad de ataque',
  castSpeed: 'Velocidad de casteo',
  critChance: 'Crítico',
  critMultiplier: 'Mult. crítico',
  hp: 'Vida',
  mana: 'Maná',
  stamina: 'Stamina',
  regenHP: 'Regen vida',
  regenMana: 'Regen maná',
  regenStamina: 'Regen energía',
  lifeStealPhysical: 'Robo de vida físico',
  lifeStealMagic: 'Robo de vida mágico',
  reflectPhysicalDamage: 'Reflejo físico',
  reflectMagicDamage: 'Reflejo mágico',
  healingOutput: 'Curación otorgada',
  healingReceived: 'Curación recibida',
  magicFind: 'Magic find',
  evasion: 'Evasión',
  // Daño por categoría (barcos). Ver docs/WATER_AND_BOATS.md.
  meleeDamagePercent: 'Daño melee',
  projectileDamagePercent: 'Daño con proyectiles',
  spellDamagePercent: 'Daño de hechizos',
  // PLACEHOLDER: bloqueado por el sistema Skill de Pesca. Al destaparlo, sacar el
  // sufijo "(próximamente)". Checklist en docs/FISHING_SKILL.md.
  fishingBonus: 'Pesca (próximamente)',
};

export function getStatLabelEs(statKey: string): string {
  return STAT_LABEL_ES_MAP[statKey] ?? statKey;
}

export interface IconLookup {
  textureKey: string;
  frame: number;
  cellSize: number;
}

/**
 * Resuelve un nombre semántico a (textureKey, frame). Recorre los atlases en
 * orden y devuelve la primera coincidencia. `null` si el nombre no existe en
 * ningún atlas.
 */
export function resolveIcon(name: string): IconLookup | null {
  for (const atlas of ICON_ATLASES) {
    const frame = atlas.icons[name];
    if (typeof frame === 'number') {
      return { textureKey: atlas.key, frame, cellSize: atlas.cellSize };
    }
  }
  return null;
}

/** Alias semántico para call-sites que arman el sprite manualmente. */
export function getIconTexture(name: string): IconLookup | null {
  return resolveIcon(name);
}

/**
 * Pinta un ícono del atlas como background sobre un elemento DOM (para paneles
 * HTML), escalando la celda a `sizePx`. Devuelve `false` si el nombre no existe.
 * Usa el mismo PNG que Phaser (`atlas.asset`), con `image-rendering: pixelated`
 * para que el pixel-art no se vea borroso.
 */
export function applyIconToDom(el: HTMLElement, name: string, sizePx: number): boolean {
  let found: { asset: string; cols: number; rows: number; frame: number } | null = null;
  for (const atlas of ICON_ATLASES) {
    const frame = atlas.icons[name];
    if (typeof frame === 'number') {
      found = { asset: atlas.asset, cols: atlas.cols, rows: atlas.rows, frame };
      break;
    }
  }
  if (!found) return false;
  const col = found.frame % found.cols;
  const row = Math.floor(found.frame / found.cols);
  el.style.width = `${sizePx}px`;
  el.style.height = `${sizePx}px`;
  el.style.backgroundImage = `url(${found.asset})`;
  el.style.backgroundRepeat = 'no-repeat';
  el.style.backgroundSize = `${found.cols * sizePx}px ${found.rows * sizePx}px`;
  el.style.backgroundPosition = `-${col * sizePx}px -${row * sizePx}px`;
  el.style.imageRendering = 'pixelated';
  return true;
}

/** Devuelve solo el frame index de un ícono por nombre, o `null` si no está mapeado. */
export function getUIIconFrame(name: string): number | null {
  return resolveIcon(name)?.frame ?? null;
}

/**
 * Texture key del primer atlas. Exportado por compatibilidad con call-sites
 * legacy que asumían un único atlas; preferí `resolveIcon()` o `getIconTexture()`
 * en código nuevo para que el call-site sea agnóstico del atlas.
 *
 * @deprecated usar resolveIcon(name) en su lugar.
 */
export const UI_ICONS_KEY = ICON_ATLASES[0].key;

/**
 * Tamaño de display default para íconos UI (px). Independiente del `cellSize`
 * del atlas source — éste sólo afecta el framing del spritesheet.
 * Subido de 16 → 24 porque a 16px los íconos quedan muy chiquitos en pantallas
 * modernas; los call-sites que necesitan más chico siguen pasando tamaño
 * explícito (11/12/14/20 para entrar en líneas de texto pequeñas).
 */
export const UI_ICON_DEFAULT_DISPLAY = 24;

/** @deprecated usar `UI_ICON_DEFAULT_DISPLAY`. */
export const UI_ICON_CELL = UI_ICON_DEFAULT_DISPLAY;

/**
 * Crea un sprite del atlas listo para usar en UI, resuelto por nombre.
 *
 * - `displaySize` opcional → default `UI_ICON_CELL` (16). Pasá un número
 *   distinto si querés escalar (ej. 14 para que entre cómodo en una línea
 *   de texto de 12px).
 * - El sprite arranca con `setScrollFactor(0)` y `setOrigin(0.5)` —
 *   apto para overlays UI que no siguen al player.
 *
 * Devuelve `null` si el nombre no está mapeado en ningún atlas o si la
 * textura no está cargada.
 */
export function createUIIcon(
  scene: Phaser.Scene,
  x: number,
  y: number,
  name: string,
  displaySize: number = UI_ICON_DEFAULT_DISPLAY,
): Phaser.GameObjects.Sprite | null {
  const r = resolveIcon(name);
  if (!r || !scene.textures.exists(r.textureKey)) return null;
  return scene.add
    .sprite(x, y, r.textureKey, r.frame)
    .setOrigin(0.5)
    .setDisplaySize(displaySize, displaySize)
    .setScrollFactor(0);
}

/**
 * Variante low-level: crea el sprite por (textureKey, frame). Útil para tools
 * (map editor) o experimentación antes de mapear el nombre en `ICON_ATLASES`.
 */
export function createUIIconByFrame(
  scene: Phaser.Scene,
  x: number,
  y: number,
  textureKey: string,
  frame: number,
  displaySize: number = UI_ICON_DEFAULT_DISPLAY,
): Phaser.GameObjects.Sprite | null {
  if (!scene.textures.exists(textureKey)) return null;
  return scene.add
    .sprite(x, y, textureKey, frame)
    .setOrigin(0.5)
    .setDisplaySize(displaySize, displaySize)
    .setScrollFactor(0);
}

/**
 * Crea un botón de cerrar con el sprite `close` del atlas, listo para usar
 * como el ✕ de paneles. Devuelve el sprite interactivo (hover cursor),
 * sin handler — el caller se lo asigna con `.on('pointerup', ...)`.
 *
 * Por convención los close buttons van en la esquina superior derecha del
 * panel — el caller pasa esa posición y elige el origin (típicamente
 * `setOrigin(1, 0)` para anclar arriba a la derecha).
 *
 * Si el atlas no está cargado, devuelve `null` y el caller debe degradar
 * (típicamente reverso al caracter `✕` como text).
 */
export function createCloseButton(
  scene: Phaser.Scene,
  x: number,
  y: number,
  size: number = UI_ICON_DEFAULT_DISPLAY,
): Phaser.GameObjects.Sprite | null {
  const sprite = createUIIcon(scene, x, y, 'close', size);
  if (!sprite) return null;
  return sprite.setInteractive({ useHandCursor: true });
}

/**
 * Valida en dev que no haya nombres duplicados entre atlases. Loggea por
 * consola las colisiones encontradas (no tira). Llamar una vez al boot del
 * juego (idealmente desde `GameScene.preload()` después de registrar los
 * spritesheets).
 */
export function validateIconAtlases(): void {
  const seen = new Map<string, string>();
  for (const atlas of ICON_ATLASES) {
    for (const name of Object.keys(atlas.icons)) {
      const prev = seen.get(name);
      if (prev) {
        console.warn(
          `[IconAtlases] Nombre '${name}' duplicado en '${prev}' y '${atlas.key}'. Gana '${prev}'.`,
        );
      } else {
        seen.set(name, atlas.key);
      }
    }
  }
}
